 
import { CustomAxiosResponse } from "@/configurations/AxiosConfig";
import { parse } from "date-fns"
import isEqual from 'lodash.isequal';

const Helpers = {
  parseCustomDate: (dateString: string | undefined | null, format = 'd-MM-yyyy') => {
    if (!dateString) return undefined;
  
    try {
      // Parse date format 'd-MM-yyyy'
      return parse(dateString, format, new Date());
    } catch (error) {
      console.error('Date parsing failed:', error);
      return undefined;
    }
  },
  dateSortingFn: (rowA: any, rowB: any, columnId: any) => {
    // Get date values from the row data structure
    // The date could be in either original or _valuesCache
    const dateAStr = rowA.original?.[columnId] || rowA._valuesCache?.[columnId]
    const dateBStr = rowB.original?.[columnId] || rowB._valuesCache?.[columnId]

    // If either date is missing, handle accordingly
    if (!dateAStr && !dateBStr) return 0
    if (!dateAStr) return -1
    if (!dateBStr) return 1

    // Parse ISO format dates
    const dateA = new Date(dateAStr)
    const dateB = new Date(dateBStr)

    // Check if dates are valid
    const isDateAValid = !isNaN(dateA.getTime())
    const isDateBValid = !isNaN(dateB.getTime())

    // Handle invalid dates
    if (!isDateAValid && !isDateBValid) return 0
    if (!isDateAValid) return -1
    if (!isDateBValid) return 1

    // Compare valid dates
    return dateA.getTime() - dateB.getTime()
  },
  isResponseSuccessful: (response: CustomAxiosResponse<any>) => response && response.status >= 200 && response.status < 300 && !response.error,
  createDateOrNull: (dateInput: string | Date | undefined | null): Date | null => {
    if (!dateInput) return null;
    try {
      const date = new Date(dateInput);
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date input provided to createDateOrNull:', dateInput);
        return null;
      }
      return date;
    } catch (error) {
      console.error('Error creating date in createDateOrNull:', error);
      return null;
    }
  },
  objectToFormData: (data: any, formData: FormData = new FormData(), parentKey = ''): FormData => {
    if (data instanceof File) {
      formData.append(parentKey, data);
    } else if (data instanceof FileList) {
      Array.from(data).forEach((file, idx) => {
        formData.append(`${parentKey}[${idx}]`, file);
      });
    } else if (data instanceof Date) {
      formData.append(parentKey, data.toISOString());
    } else if (Array.isArray(data)) {
      if (parentKey) {
        formData.append(parentKey, JSON.stringify(data));
      }
    } else if (typeof data === 'object' && data !== null) {
      Object.entries(data).forEach(([key, value]) => {
        Helpers.objectToFormData(value, formData, parentKey ? `${parentKey}.${key}` : key);
      });
    } else if (data !== undefined && data !== null && parentKey) {
      formData.append(parentKey, data);
    }
    return formData;
  },
  toOptionsArray: <T>(
    rawArray: T[] | undefined,
    valueKey: keyof T,
    labelKey?: keyof T,
    labelFn?: (item: T) => string,
    extraFields?: (item: T) => Record<string, any>
  ): { value: string; label: string }[] => {
    const arr = Array.isArray(rawArray) ? rawArray?.map(item => ({
      value: String(item[valueKey]),
      label: labelFn
        ? labelFn(item)
        : labelKey
          ? String(item[labelKey])
          : '',
      ...(extraFields ? extraFields(item) : {})
    })) : [];
    return arr.sort((a, b) => a.label.localeCompare(b.label));
  },
  secondsToDate(seconds?: number): Date {
    // Geçersiz değer kontrolü
    if (typeof seconds !== "number" || isNaN(seconds)) {
      return new Date(Number.NaN) // Geçersiz tarih döndür
    }
    if (seconds === 0) return new Date(0);

    // 1970-01-01T00:00:00.000Z UTC başlangıç tarihi
    const date = new Date(Date.UTC(1970, 0, 1, 0, 0, 0, 0));
    date.setUTCSeconds(seconds);

    return date;
  },

  dateToSeconds(date?: Date): number {
    if (!(date instanceof Date) || isNaN(date.getTime())) return NaN;
    // UTC saat ve dakika değerlerini al (UTC+0'a göre)
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    return (hours * 60 + minutes) * 60;
  },
  formatDurationTurkish(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const parts = [];
    if (h > 0) parts.push(`${h} saat`);
    if (m > 0) parts.push(`${m} dakika`);
    if (s > 0 || parts.length === 0) parts.push(`${s} saniye`);
    return parts.join(' ');
  },
  decimalToHour: (decimal: number) => {
    if (typeof decimal !== "number" || isNaN(decimal)) return "-";
    const sign = decimal < 0 ? '-' : '';
    const abs = Math.abs(decimal);
    const hours = Math.floor(abs);
    const minutes = Math.round((abs - hours) * 60);
    return `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
  },
  minutesToSeconds(minutes: number): number {
    if (typeof minutes !== "number" || isNaN(minutes)) return NaN;
    return Math.round(minutes * 60);
  },
  getChangedValues: (initialValues: any, currentValues: any) => {
    const changedValues = {};

    Object.keys(currentValues).forEach(key => {
      const initialValue = initialValues[key];
      const currentValue = currentValues[key];
      const initialValueIsEmpty = initialValue === null || initialValue === undefined || initialValue === '';
      const currentValueIsEmpty = currentValue === null || currentValue === undefined || currentValue === '';

      // Boş opsiyonel alanların farklı temsilleri (null, undefined, '') bir değişiklik değildir.
      // Böylece yalnızca proje/şube güncellendiğinde boş kullanıcı alanları payload'a eklenmez.
      if (initialValueIsEmpty && currentValueIsEmpty) {
        return;
      }

      // lodash.isEqual kullanarak derin karşılaştırma (nested object/array için önemli)
      // Eğer basit bir karşılaştırma yeterliyse: if (initialValue !== currentValue)
      if (!isEqual(initialValue, currentValue)) {
        (changedValues as any)[key] = currentValue;
      }
    });

    return changedValues;
  }
}

export default Helpers;